// src/hooks/useStreamingChat.ts
import { useState, useCallback, useRef, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "../services/firebase";
import { Message, getMessageText, getMessageImages } from "../types";
import { CompleteUserProfile } from "../types/profile";
import { getLanguageHint } from "../utils/getLanguageHint";

interface StreamingChatOptions {
  onStreamStart?: () => void;
  onToken?: (token: string, fullContent: string) => void;
  onStreamEnd?: (fullContent: string) => void;
  onAudioReady?: (audioUrl: string) => void;
  onComplete?: (content: string, audioUrl?: string) => void;
  onError?: (error: string) => void;
  onStatus?: (status: string) => void;
  onLandmarkVerified?: (
    verification: LandmarkVerification,
    visitSaved: boolean,
    visitId?: string
  ) => void;
  // NEW: Callback when server confirms it saved the assistant message
  onAssistantMessageSaved?: (messageId: string) => void;
}

interface LandmarkVerification {
  decision: "verified" | "uncertain" | "not_landmark";
  confidence: number;
  landmark_name?: string;
  landmark_type?:
    | "monument"
    | "museum"
    | "natural"
    | "religious"
    | "architectural"
    | "cultural"
    | "historical"
    | "other";
  reasoning?: string;
  coordinates_match?: boolean;
  visit_worthy?: boolean;
}

interface StreamingChatState {
  isStreaming: boolean;
  streamingContent: string;
  error: string | null;
  status: string;
}

interface ConversationContext {
  role: "user" | "assistant";
  content: string;
  images?: string[];
  timestamp?: string;
  location?: {
    latitude?: number;
    longitude?: number;
    geohash?: string;
  };
}

export const useStreamingChat = (options: StreamingChatOptions = {}) => {
  const [state, setState] = useState<StreamingChatState>({
    isStreaming: false,
    streamingContent: "",
    error: null,
    status: "",
  });

  const completionTriggeredRef = useRef(false);

  // ── Stream lifecycle controls ──────────────────────────────────
  // Without these, an in-flight stream survives component unmount
  // and route navigation: the fetch keeps draining bytes, the
  // reader loop keeps running, onComplete fires on a phantom
  // component, and we burn server tokens for a UI nobody is
  // looking at. Three pieces:
  //   - abortRef: AbortController bound to fetch + reader. Calling
  //     .abort() rejects the fetch promise and unblocks
  //     reader.read() with a DOMException('AbortError').
  //   - mountedRef: guards every setState / callback dispatch so a
  //     late-arriving token doesn't try to set state on an
  //     unmounted component.
  //   - silenceTimerRef: per-token watchdog. If the server goes
  //     silent for SILENCE_LIMIT_MS, we abort. Cloud Functions
  //     have a hard wall clock around 540s; we'd rather fail loud
  //     at 90s of nothing than appear to hang for several minutes.
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Per-token silence cap. Tuned for a chatty model that emits
   *  ~one token every few hundred ms once it gets going; the most
   *  common cause of >30s gaps is a stuck function or upstream API
   *  hiccup, both of which we'd rather surface than wait out. */
  const SILENCE_LIMIT_MS = 90_000;

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      // Abort the in-flight stream. Downstream catch block reports
      // the error and cleans up state.
      try {
        abortRef.current?.abort();
      } catch {
        /* ignore */
      }
    }, SILENCE_LIMIT_MS);
  }, [clearSilenceTimer]);

  // Cleanup on unmount: abort any in-flight stream, mark unmounted
  // so the rest of the closure stops touching state. Without this,
  // navigating away from /chat mid-stream leaks the connection
  // until the server times out.
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearSilenceTimer();
      try {
        abortRef.current?.abort();
      } catch {
        /* ignore */
      }
    };
  }, [clearSilenceTimer]);

  // Load recent conversation context — includes location metadata per
  // message so the AI can reason about places discussed earlier in the
  // conversation, even after the user has moved to a new location.
  const loadConversationContext = async (
    conversationId: string
  ): Promise<ConversationContext[]> => {
    try {
      const messagesRef = collection(
        db,
        "conversations",
        conversationId,
        "messages"
      );
      // Last 30 messages is ~15 turns. This is the sliding window the AI sees
      // directly. Older messages should be compressed into a conversation
      // summary stored on the conversation doc (future: background function).
      const q = query(messagesRef, orderBy("created_at", "desc"), limit(30));
      const snapshot = await getDocs(q);

      const context: ConversationContext[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        const message: Message = {
          id: doc.id,
          role: data.role,
          parts: data.parts,
          content: data.content, // Legacy support
          image_url: data.image_url, // Legacy support
          created_at: data.created_at?.toDate() || new Date(),
          location: data.location,
        };

        // Skip system messages — these are server-written UI cards
        // (collection reveals, completion celebrations) that exist
        // for the renderer, not for the LLM. Sending them as
        // conversation history would (a) confuse models that
        // reserve 'system' role for the prompt, and (b) waste
        // context budget on text the assistant didn't author.
        if (message.role !== "user" && message.role !== "assistant") {
          return;
        }
        // After this guard, message.role is narrowed to
        // 'user' | 'assistant' so the ConversationContext
        // assignment below is type-safe.
        const role: "user" | "assistant" = message.role;

        const textContent = getMessageText(message);
        const images = getMessageImages(message);

        if (textContent || images.length > 0) {
          const ctx: ConversationContext = {
            role,
            content: textContent,
            images: images.map((img) => img.url),
            timestamp: message.created_at.toISOString(),
          };
          // Preserve location tag per-message so the backend can reason
          // about where the user was when they sent each message.
          const loc = (data.location || {}) as any;
          if (loc.latitude != null && loc.longitude != null) {
            ctx.location = {
              latitude: loc.latitude,
              longitude: loc.longitude,
              geohash: loc.geohash,
            };
          } else if (loc.geohash) {
            ctx.location = { geohash: loc.geohash };
          }
          context.push(ctx);
        }
      });

      // Reverse to get chronological order (oldest first)
      return context.reverse();
    } catch (error) {
      console.error("Error loading conversation context:", error);
      return [];
    }
  };

  // Load user bio context and instructions from profile
  const loadUserBioContext = async (): Promise<CompleteUserProfile | null> => {
    try {
      const user = auth.currentUser;
      if (!user) return null;

      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) return null;

      const userData = userDoc.data();
      const travelProfile = userData.travelProfile;

      if (!travelProfile) return null;

      // Return the complete travel profile as it already has the right structure
      const profile: CompleteUserProfile = {
        demographics: travelProfile.demographics || {},
        travelPreferences: travelProfile.travelPreferences || {},
        interests: travelProfile.interests || {},
        constraints: travelProfile.constraints || {},
        completedAt: travelProfile.completedAt?.toDate() || null,
        lastUpdated: travelProfile.lastUpdated?.toDate() || new Date(),
      };

      // Only return if the profile has meaningful data (not just defaults)
      const hasData =
        (profile.demographics &&
          Object.values(profile.demographics).some((v) => v !== null)) ||
        (profile.travelPreferences &&
          Object.values(profile.travelPreferences).some((v) =>
            Array.isArray(v) ? v.length > 0 : v !== null
          )) ||
        (profile.interests &&
          Object.values(profile.interests).some((v) =>
            Array.isArray(v) ? v.length > 0 : v !== null
          )) ||
        (profile.constraints &&
          Object.values(profile.constraints).some((v) => v !== null));

      return hasData ? profile : null;
    } catch (error) {
      console.error("Error loading user bio context:", error);
      return null;
    }
  };

  // Load user custom instructions
  const loadUserInstructions = async (): Promise<string | null> => {
    try {
      const user = auth.currentUser;
      if (!user) return null;

      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) return null;

      const userData = userDoc.data();
      const customInstructions = userData.customInstructions;

      // Return instructions if they exist and have content
      return customInstructions &&
        typeof customInstructions === "string" &&
        customInstructions.trim()
        ? customInstructions.trim()
        : null;
    } catch (error) {
      console.error("Error loading user instructions:", error);
      return null;
    }
  };

  const sendStreamingMessage = useCallback(
    async (
      message: string,
      conversationId: string,
      imagePath?: string,
      location?: any
    ) => {
      if (!auth.currentUser) {
        throw new Error("Not authenticated");
      }

      // Reset all completion trackers
      completionTriggeredRef.current = false;

      console.log("Starting new streaming request - resetting all flags");

      // Reset state
      setState({
        isStreaming: true,
        streamingContent: "",
        error: null,
        status: "Loading conversation context...",
      });

      try {
        // Load conversation context
        console.log("Loading conversation context...");
        const conversationHistory = await loadConversationContext(
          conversationId
        );
        console.log("Loaded context messages:", conversationHistory.length);

        // Load user bio context
        console.log("Loading user bio context...");
        const userBioContext = await loadUserBioContext();
        console.log("User bio context loaded:", userBioContext ? "Yes" : "No");

        // Load user custom instructions
        console.log("Loading user instructions...");
        const userInstructions = await loadUserInstructions();
        console.log(
          "User instructions loaded:",
          userInstructions ? "Yes" : "No"
        );

        // Get auth token
        const authToken = await auth.currentUser.getIdToken();

        // Prepare request data with conversation context, user bio, and custom instructions
        // NOTE: We still send client_saved_messages: true for user messages,
        // but the server will now ALWAYS save assistant messages regardless
        const requestData = {
          message,
          conversation_id: conversationId,
          conversation_history: conversationHistory, // Include full context
          user_bio_context: userBioContext, // Add user bio context
          user_instructions: userInstructions, // Add custom user instructions
          auth_token: authToken,
          image_path: imagePath,
          location,
          // Client saves user messages, server always saves assistant messages
          client_saved_messages: true,
          // Language hint for AI output. Server prefers
          // user_bio_context.demographics.primaryLanguage; this is the
          // fallback for anonymous / pre-onboarding sessions where the
          // bio context has no language set. Browser locale (e.g.
          // "en-US", "es-ES") gets normalised server-side.
          language: getLanguageHint(
            userBioContext
              ? ({
                  travelProfile: userBioContext,
                } as any)
              : null
          ),
        };

        setState((prev) => ({ ...prev, status: "Connecting to AI..." }));

        // Create fetch request for streaming
        const functionUrl =
          "https://us-central1-travelx-46b7a.cloudfunctions.net/streamTourGuide";

        // Abort any previous in-flight stream before starting a new
        // one. Belt-and-suspenders against a rapid double-send: even
        // though the UI should disable Send while streaming, a
        // programmatic re-trigger (retry on a stale closure) could
        // otherwise leave two readers churning on the same hook.
        try {
          abortRef.current?.abort();
        } catch {
          /* ignore */
        }
        const controller = new AbortController();
        abortRef.current = controller;
        armSilenceTimer();

        const response = await fetch(functionUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            "Cache-Control": "no-cache",
          },
          body: JSON.stringify(requestData),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error("No response body");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let currentContent = "";

        options.onStreamStart?.();

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            clearSilenceTimer();
            console.log("Stream reader done, checking completion status");

            // Complete immediately when stream ends
            if (
              !completionTriggeredRef.current &&
              currentContent &&
              options.onComplete &&
              mountedRef.current
            ) {
              console.log("Triggering completion from stream end");

              completionTriggeredRef.current = true;

              setState((prev) => ({
                ...prev,
                status: "Response complete",
              }));

              // Complete - server has already saved the message
              await options.onComplete(currentContent);

              if (mountedRef.current) {
                setState((prev) => ({
                  ...prev,
                  isStreaming: false,
                  status: "Complete",
                }));
              }
            }
            break;
          }

          // We got bytes — reset the silence watchdog. Even a
          // heartbeat comment from the server resets it; this is
          // about "the connection is alive" rather than "useful
          // tokens are arriving".
          armSilenceTimer();

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              continue;
            }

            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));

                // Handle different event types
                if (data.content && data.full_content !== undefined) {
                  // Token event - update UI immediately
                  currentContent = data.full_content;

                  setState((prev) => ({
                    ...prev,
                    streamingContent: data.full_content,
                    status: "AI is responding...",
                  }));

                  options.onToken?.(data.content, data.full_content);
                }
                // Handle landmark verification event
                else if (data.landmark_verification) {
                  console.log(
                    "🏛️ Received landmark verification from stream:",
                    data.landmark_verification
                  );

                  // Call the landmark verification callback immediately
                  if (options.onLandmarkVerified) {
                    options.onLandmarkVerified(
                      data.landmark_verification.verification,
                      data.landmark_verification.visit_saved,
                      data.landmark_verification.visit_id
                    );
                  }
                }
                // NEW: Handle server confirmation of assistant message save
                else if (data.assistant_message_saved) {
                  console.log(
                    "📝 Server confirmed assistant message saved:",
                    data.assistant_message_id
                  );

                  if (options.onAssistantMessageSaved && data.assistant_message_id) {
                    options.onAssistantMessageSaved(data.assistant_message_id);
                  }
                }
                else if (
                  data.message &&
                  data.message.includes("Starting AI response")
                ) {
                  setState((prev) => ({
                    ...prev,
                    status: "AI is analyzing context and responding...",
                  }));
                } else if (
                  data.message &&
                  data.message.includes("AI response completed")
                ) {
                  console.log("Received stream end signal");
                  options.onStreamEnd?.(currentContent);
                  setState((prev) => ({
                    ...prev,
                    status: "Response complete",
                  }));
                } else if (
                  data.message &&
                  data.message.includes("Request completed successfully")
                ) {
                  // Final completion - trigger immediately without waiting for audio
                  console.log("Final completion message received");

                  if (!completionTriggeredRef.current && options.onComplete) {
                    const finalContent = data.content || currentContent;

                    console.log("Triggering completion from final message");

                    completionTriggeredRef.current = true;

                    setState((prev) => ({
                      ...prev,
                      status: "Complete",
                    }));

                    // Complete - server has already saved the message
                    await options.onComplete(finalContent);

                    setState((prev) => ({
                      ...prev,
                      isStreaming: false,
                      status: "Complete",
                    }));

                    return; // Exit completely
                  }
                } else if (data.error) {
                  throw new Error(data.error);
                } else if (data.message) {
                  setState((prev) => ({
                    ...prev,
                    status: data.message,
                  }));
                  options.onStatus?.(data.message);
                }
              } catch (parseError) {
                console.warn("Failed to parse SSE data:", line, parseError);
              }
            }
          }
        }
      } catch (error: any) {
        clearSilenceTimer();

        // AbortError is a normal lifecycle signal (unmount,
        // navigation, user-initiated stop, silence-timer fire).
        // Don't surface it as a user-facing error and don't try to
        // setState if we've already unmounted.
        const isAbort =
          error?.name === "AbortError" ||
          error?.code === 20 || // DOMException.ABORT_ERR
          /aborted/i.test(error?.message || "");

        if (!mountedRef.current) {
          // Component is gone — silently swallow. The cleanup
          // useEffect already aborted; this catch is just the
          // tail end of the unblocked reader.
          return;
        }

        if (isAbort) {
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            status: "Stopped",
          }));
          return;
        }

        console.error("Streaming error:", error);
        const errorMessage = error.message || "Failed to stream response";

        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: errorMessage,
          status: "Error",
        }));

        options.onError?.(errorMessage);
      } finally {
        // Drop the controller reference once the request is over so
        // the next send doesn't try to abort a dead one.
        if (abortRef.current) {
          abortRef.current = null;
        }
      }
    },
    [options, armSilenceTimer, clearSilenceTimer]
  );

  const stopStreaming = useCallback(() => {
    clearSilenceTimer();
    try {
      abortRef.current?.abort();
    } catch {
      /* ignore */
    }
    setState((prev) => ({
      ...prev,
      isStreaming: false,
      status: "Stopped",
    }));
  }, [clearSilenceTimer]);

  return {
    ...state,
    sendStreamingMessage,
    stopStreaming,
  };
};